import { lazy, Suspense, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@shared/v2/Alert";
import { SpinnerSolidV6 } from "@shared/v2/Icomoon";
import dayjs from "@shared/dayjs";
import { DateRangeDropdown } from "@shared/v2/DateRangeDropdown";
import { Dropdown } from "../../shared/v1";
import { statCardsTotal } from "./appointmentsReportHelper";
import {
  ReportHeaderSortContainer,
  UserTaskHolder,
  AgentReportRow,
  AgentReportTotalRow,
  LeadSourcesHolder,
  AllOrDateRangeRadios,
} from ".";
import AgentCallsHolder from "./AgentCallsHolder";
import AgentTextsHolder from "./AgentTextsHolder";
import AppointmentsStatCards from "./AppointmentsStatCards";
import AppointmentsContainer from "./AppointmentsContainer";
import ApptTypeFilter from "./ApptTypeFilter";
import BusinessDashboard from "./BusinessDashboard";
import SalesFunnelDashboard from "./SalesFunnelDashboard/SalesFunnelDashboard";
import AgentDashboard from "./AgentDashboard";
import OperatorDashboard from "./OperatorDashboard/OperatorDashboard";
import Leaderboards from "./Leaderboards/Leaderboards";
import useSimpleNavigation from "../../shared/v2/useSimpleNavigation";
import OpenHouseDashboard from "./OpenHouseDashboard";
import Referrals from "./Referrals";

// powerbi-client is only needed for the Financial Dashboard reports
const PowerBIReport = lazy(() => import("./FinancialDashboard/PowerBIReport"));
import { Table, TBody, THead } from "./BusinessDashboard/Table/Table";

const ACTIVE_REPORT_TITLE = {
  appointments: "Appointment Tracking",
  leadSource: "Lead Source",
  tasks: "Tasks",
  agentActivity: "Agent Activity",
  agentCalls: "Call Activity",
  agentTexts: "Text Activity",
};

const ACTIVE_REPORT_DESCRIPTION = {
  leadSource: "Track the number of leads and status pipeline of all your leads.",
  tasks: "Track the number of assigned and completed tasks in a given date range.",
  agentActivity: "Track agent activity for leads newly assigned in a given date range.",
  agentCalls: "Track agent call statistics for a given date range.",
  agentTexts: "Track agent text statistics for a given date range.",
};

const SOURCE_TYPE_TITLE = {
  leadSource: "Brivity Sources",
  tasks: "Administrators",
};

const LEGACY_REPORT_DATE_CONFIG = {
  disableFutureDates: true,
  defaultOption: "last_7_days",
};

// Persisted `dateRange` may still carry pre-Phase-3 tokens; map them so the
// shared DateRangeDropdown displays the correct initial selection.
const LEGACY_TOKEN_ALIASES = {
  last_seven_days: "last_7_days",
  last_fourteen_days: "last_14_days",
  last_thirty_days: "last_30_days",
};

const toCanonicalToken = (value) => LEGACY_TOKEN_ALIASES[value] || value;

const toIsoOrNull = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const Reports = (props) => {
  const {
    activeFilters,
    adminTasks,
    adminTotalCompletedTasks,
    adminTotalLateTasks,
    adminTotalOnTimeTasks,
    adminTotalTasks,
    agentList,
    agentPersonList,
    agents,
    agentTasks,
    agentTotalCompletedTasks,
    agentTotalLateTasks,
    agentTotalOnTimeTasks,
    agentTotalTasks,
    appointmentData,
    brivitySources,
    brivityStatusCounts,
    brivityTotalLeadSourceCount,
    callData,
    currentUserId,
    customDates,
    customRange,
    dateRange,
    error,
    fetchNewData,
    handleCustomDaySelect,
    handleDateRangeSelect,
    isActivityReportLoaded,
    isAdmin,
    isAllAppointmentsInRange,
    isAllTasksInRange,
    isFetching,
    isPlaceUser,
    operatorDashboardEnabled,
    operatorDashboardGraphsEnabled,
    otherSources,
    otherStatusCounts,
    otherTotalLeadSourceCount,
    powerbiEnabled,
    powerbiSecondAuth,
    basePowerbiEnabled,
    basePowerbiSecondAuth,
    setError,
    setIsFetching,
    setTasksConstraint,
    sortBy,
    sortDesc,
    sortKey,
    textData,
    userId,
  } = props;

  const scrollContainer = useRef(null);
  const {
    query: { report_tab: activeReport },
    push,
  } = useSimpleNavigation();
  let isAllInRange = false;
  if (activeReport === "tasks") isAllInRange = isAllTasksInRange;
  if (activeReport === "appointments") isAllInRange = isAllAppointmentsInRange;

  const [apptFilters, _setApptFilters] = useState({
    isBuyer:
      JSON.parse(localStorage.getItem("isBuyer")) === null
        ? true
        : JSON.parse(localStorage.getItem("isBuyer")),
    isBuyerSeller:
      JSON.parse(localStorage.getItem("isBuyerSeller")) === null
        ? true
        : JSON.parse(localStorage.getItem("isBuyerSeller")),
    isSeller:
      JSON.parse(localStorage.getItem("isSeller")) === null
        ? true
        : JSON.parse(localStorage.getItem("isSeller")),
    isRecruit:
      JSON.parse(localStorage.getItem("isRecruit")) === null
        ? true
        : JSON.parse(localStorage.getItem("isRecruit")),
    isOther:
      JSON.parse(localStorage.getItem("isOther")) === null
        ? true
        : JSON.parse(localStorage.getItem("isOther")),
  });

  const setApptFilters = (newState) => _setApptFilters((prevState) => ({ ...prevState, ...newState }));

  // Does an emitted selection already match what's in the store?
  //
  // `DateRangeDropdown` fires `onChange` on mount, and it mounts more than
  // once: it renders only for non-dashboard reports, so switching to a
  // dashboard tab and back remounts it. Comparing against the store covers
  // every one of those fires, where a one-shot "skip the first" flag would
  // only cover the first and let later remounts trigger redundant fetches.
  const matchesCurrentSelection = (value, resolvedRange) => {
    if (value !== toCanonicalToken(dateRange)) return false;
    // Same token, but Custom can still describe a different window.
    if (value !== "custom") return true;
    return (
      resolvedRange.startAt === toIsoOrNull(customDates.start) &&
      resolvedRange.endAt === toIsoOrNull(customDates.end)
    );
  };

  const handleReportDateRangeChange = (value, resolvedRange, meta) => {
    if (!meta.isValid) return;
    if (matchesCurrentSelection(value, resolvedRange)) return;
    setIsFetching(true);
    if (value === "custom") {
      const range = { start: resolvedRange.startAt, end: resolvedRange.endAt };
      handleCustomDaySelect(range);
      fetchNewData("custom", activeReport, userId, activeFilters, range, isAllInRange);
    } else {
      handleDateRangeSelect(value, activeReport, userId, activeFilters, isAllInRange);
    }
  };

  const handleAgentActivityViewSwitch = () => {
    if (isActivityReportLoaded === false) {
      setIsFetching(true);
      fetchNewData(
        localStorage.getItem("date_range") !== null ? localStorage.getItem("date_range") : "last_seven_days",
        "agentActivity",
      );
    }
  };

  const handleAgentCallsViewSwitch = () => {
    if (callData.length === 0) {
      setIsFetching(true);
      fetchNewData(
        localStorage.getItem("date_range") !== null ? localStorage.getItem("date_range") : "last_seven_days",
        "agentCalls",
      );
    }
  };

  const handleAgentTextsViewSwitch = () => {
    if (textData.length === 0) {
      setIsFetching(true);
      fetchNewData(
        localStorage.getItem("date_range") !== null ? localStorage.getItem("date_range") : "last_seven_days",
        "agentTexts",
      );
    }
  };

  const handleTaskViewSwitch = () => {
    if (adminTasks.length === 0 || agentTasks.length === 0) {
      setIsFetching(true);
      fetchNewData(
        localStorage.getItem("date_range") !== null ? localStorage.getItem("date_range") : "last_seven_days",
        "tasks",
      );
    }
  };

  const handleAppointmentsViewSwitch = () => {
    if (!appointmentData.length) {
      setIsFetching(true);

      fetchNewData(
        localStorage.getItem("date_range") !== null ? localStorage.getItem("date_range") : "last_seven_days",
        "appointments",
      );
    }
  };

  const handleLeadSourceViewSwitch = () => {
    if (brivitySources.length === 0) {
      setIsFetching(true);
      fetchNewData(
        localStorage.getItem("date_range") !== null ? localStorage.getItem("date_range") : "last_seven_days",
        "leadSource",
      );
    }
  };

  useEffect(() => {
    if (!activeReport) {
      push("/agent_stats?report_tab=appointments");
      return;
    }

    scrollContainer.current?.firstChild?.scrollIntoView();

    switch (activeReport) {
      case "agentCalls": {
        handleAgentCallsViewSwitch();
        break;
      }
      case "agentTexts": {
        handleAgentTextsViewSwitch();
        break;
      }
      case "leadSource": {
        handleLeadSourceViewSwitch();
        break;
      }
      case "tasks": {
        handleTaskViewSwitch();
        break;
      }
      case "appointments": {
        handleAppointmentsViewSwitch();
        break;
      }
      default:
        handleAgentActivityViewSwitch();
        break;
    }
  }, [activeReport]);

  const handleAppointmentType = (type) => {
    if (type === "Buyer") {
      localStorage.setItem("isBuyer", JSON.stringify(!apptFilters.isBuyer));
      setApptFilters({ isBuyer: !apptFilters.isBuyer });
    }

    if (type === "Seller") {
      localStorage.setItem("isSeller", JSON.stringify(!apptFilters.isSeller));
      setApptFilters({ isSeller: !apptFilters.isSeller });
    }
    if (type === "Buyer/Seller") {
      localStorage.setItem("isBuyerSeller", JSON.stringify(!apptFilters.isBuyerSeller));
      setApptFilters({ isBuyerSeller: !apptFilters.isBuyerSeller });
    }
    if (type === "Recruit") {
      localStorage.setItem("isRecruit", JSON.stringify(!apptFilters.isRecruit));
      setApptFilters({ isRecruit: !apptFilters.isRecruit });
    }
    if (type === "Other") {
      localStorage.setItem("isOther", JSON.stringify(!apptFilters.isOther));
      setApptFilters({ isOther: !apptFilters.isOther });
    }
  };

  const handleTaskConstraintChange = (_reportType, allTasksInRange) => {
    setIsFetching(true);
    setTasksConstraint(allTasksInRange);
    fetchNewData(dateRange, activeReport, null, null, customDates, allTasksInRange);
  };

  const leadStats = statCardsTotal(apptFilters, appointmentData, dateRange);

  const isDashboardReport = [
    "operatorDashboard",
    "businessDashboard",
    "salesFunnelDashboard",
    "agentDashboard",
    "leaderboards",
    "openHouseDashboard",
    "powerbi",
    "basePowerbi",
    "referrals",
  ].includes(activeReport);

  return (
    <div ref={scrollContainer} className="tw-relative tw-h-full tw-overflow-x-auto tw-flex tw-flex-col">
      {activeReport === "referrals" && <Referrals />}

      {powerbiEnabled && activeReport === "powerbi" && (
        <Suspense fallback={null}>
          <PowerBIReport
            powerbiSecondAuth={powerbiSecondAuth}
            url="/api/v4/reporting/financial_dashboard_embed_config"
          />
        </Suspense>
      )}

      {basePowerbiEnabled && activeReport === "basePowerbi" && (
        <Suspense fallback={null}>
          <PowerBIReport
            powerbiSecondAuth={basePowerbiSecondAuth}
            url="/api/v4/reporting/base_financial_dashboard_embed_config"
          />
        </Suspense>
      )}

      {operatorDashboardEnabled && activeReport === "operatorDashboard" && (
        <OperatorDashboard operatorDashboardGraphsEnabled={operatorDashboardGraphsEnabled} />
      )}

      {isAdmin && activeReport === "businessDashboard" && <BusinessDashboard />}

      {activeReport === "agentDashboard" && <AgentDashboard agentList={agentPersonList} isAdmin={isAdmin} />}

      {activeReport === "leaderboards" && (
        <Leaderboards isPlaceUser={isPlaceUser} currentUserId={currentUserId} />
      )}

      {activeReport === "salesFunnelDashboard" && <SalesFunnelDashboard />}

      {activeReport === "openHouseDashboard" && <OpenHouseDashboard />}

      {!isDashboardReport && (
        <div
          id="content"
          className="tw-h-full tw-overflow-x-auto !tw-p-[16px] tw-flex tw-flex-col tw-gap-[12px]"
        >
          {isFetching && (
            <div className="tw-z-3 tw-absolute tw-bg-white/50 tw-inset-0 tw-flex tw-justify-center tw-items-center">
              <SpinnerSolidV6 className="tw-animate-spin tw-w-[48px] tw-h-[48px]" size="custom" />
            </div>
          )}

          {!isFetching && error && (
            <div className="tw-mb-[12px]">
              <Alert
                variant="error"
                title="Whoops, something went wrong."
                text="There was a problem loading the data, please try again later or contact support."
                onClose={() => setError(false)}
              />
            </div>
          )}

          <div className="tw-flex tw-flex-col tw-gap-[12px]">
            <div className="tw-bg-white tw-p-[16px]">
              <h3 className="!tw-m-0 tw-text-24d">{ACTIVE_REPORT_TITLE[activeReport]}</h3>
            </div>
            <div className="tw-bg-white tw-p-[16px] tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-[8px]">
              {activeReport === "tasks" && (
                <AllOrDateRangeRadios
                  report={activeReport}
                  allInRange={isAllInRange}
                  onChange={handleTaskConstraintChange}
                />
              )}

              {activeReport === "appointments" && (
                <ApptTypeFilter apptFilters={apptFilters} handleAppointmentType={handleAppointmentType} />
              )}

              {!["appointments", "tasks"].includes(activeReport) && (
                <h6 className="!tw-m-0 tw-text-14d">{ACTIVE_REPORT_DESCRIPTION[activeReport]}</h6>
              )}
              {activeReport === "leadSource" && agentList.length > 1 && (
                <div className="tw-flex tw-items-center tw-gap-[8px]">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="tw-flex-shrink-0 tw-mb-0">Filter by user</label>
                  <Dropdown
                    className="!tw-w-[300px]"
                    value={userId || "all"}
                    options={agentList}
                    isSearchable
                    menuShouldScrollIntoView
                    onChange={(e) => fetchNewData(dateRange, activeReport, e.value, null, customDates)}
                    isClearable={false}
                  />
                </div>
              )}
              <div className="tw-whitespace-nowrap tw-flex tw-items-center tw-gap-[8px]">
                <h6>Date Range</h6>
                <DateRangeDropdown
                  mode="standard"
                  config={LEGACY_REPORT_DATE_CONFIG}
                  initialValue={toCanonicalToken(dateRange)}
                  initialCustomRange={
                    dateRange === "custom"
                      ? { startAt: toIsoOrNull(customDates.start), endAt: toIsoOrNull(customDates.end) }
                      : undefined
                  }
                  onChange={handleReportDateRangeChange}
                />
              </div>
            </div>
            {activeReport === "appointments" && <AppointmentsStatCards leadStats={leadStats} />}
          </div>

          <div className="!tw-m-[0px] tw-relative tw-overflow-auto">
            <Table className="tw-bg-white">
              <THead>
                {((activeReport === "tasks" && adminTasks.length > 0) ||
                  (activeReport !== "tasks" && activeReport !== "appointments")) && (
                  <ReportHeaderSortContainer
                    sortDesc={sortDesc}
                    sourceTypeTitle={SOURCE_TYPE_TITLE[activeReport]}
                    isOtherSources={false}
                    activeReport={activeReport}
                    sortKey={sortKey}
                    sortBy={sortBy}
                  />
                )}
              </THead>

              <TBody>
                {activeReport === "agentActivity" && (
                  <>
                    {agents.map((agent) => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <AgentReportRow key={agent.id} report={agent} {...props} />
                    ))}
                    {agents.length > 1 && (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <AgentReportTotalRow {...props} />
                    )}
                  </>
                )}

                {activeReport === "appointments" && (
                  <AppointmentsContainer
                    sortDesc={sortDesc}
                    sortKey={sortKey}
                    sortBy={sortBy}
                    appointmentData={appointmentData}
                    apptFilters={apptFilters}
                  />
                )}

                {activeReport === "leadSource" && (
                  <>
                    <LeadSourcesHolder
                      statusCounts={brivityStatusCounts}
                      totalLeadCount={brivityTotalLeadSourceCount}
                      sources={brivitySources}
                    />
                    <ReportHeaderSortContainer
                      sortDesc={sortDesc}
                      sourceTypeTitle="Other Sources"
                      isOtherSources
                      activeReport={activeReport}
                      sortKey={sortKey}
                      sortBy={sortBy}
                    />
                    <LeadSourcesHolder
                      isOtherSource
                      statusCounts={otherStatusCounts}
                      totalLeadCount={otherTotalLeadSourceCount}
                      sources={otherSources}
                    />
                  </>
                )}

                {activeReport === "agentCalls" && <AgentCallsHolder agentCallData={props} />}

                {activeReport === "agentTexts" && <AgentTextsHolder agentTextData={props} />}

                {activeReport === "tasks" && (
                  <>
                    {adminTasks.length > 0 && (
                      <UserTaskHolder
                        agents={adminTasks}
                        totals={adminTotalTasks}
                        completedTotal={adminTotalCompletedTasks}
                        onTimeTotal={adminTotalOnTimeTasks}
                        lateTotal={adminTotalLateTasks}
                        isAdmin
                      />
                    )}
                    <ReportHeaderSortContainer
                      sortDesc={sortDesc}
                      sourceTypeTitle="Agents"
                      isAgentTasks
                      activeReport={activeReport}
                      sortKey={sortKey}
                      sortBy={sortBy}
                    />
                    <UserTaskHolder
                      isAdmin={false}
                      agents={agentTasks}
                      totals={agentTotalTasks}
                      completedTotal={agentTotalCompletedTasks}
                      onTimeTotal={agentTotalOnTimeTasks}
                      lateTotal={agentTotalLateTasks}
                    />
                  </>
                )}
              </TBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

Reports.propTypes = {
  activeFilters: PropTypes.shape({}).isRequired,
  adminTasks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  adminTotalCompletedTasks: PropTypes.number.isRequired,
  adminTotalLateTasks: PropTypes.number.isRequired,
  adminTotalOnTimeTasks: PropTypes.number.isRequired,
  adminTotalTasks: PropTypes.number.isRequired,
  agentList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  agentPersonList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  agents: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  agentTasks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  agentTotalCompletedTasks: PropTypes.number.isRequired,
  agentTotalLateTasks: PropTypes.number.isRequired,
  agentTotalOnTimeTasks: PropTypes.number.isRequired,
  agentTotalTasks: PropTypes.number.isRequired,
  appointmentData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  brivitySources: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  brivityStatusCounts: PropTypes.shape().isRequired,
  brivityTotalLeadSourceCount: PropTypes.number.isRequired,
  callData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  currentUserId: PropTypes.number.isRequired,
  customDates: PropTypes.shape().isRequired,
  customRange: PropTypes.bool.isRequired,
  dateRange: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  fetchNewData: PropTypes.func.isRequired,
  handleCustomDaySelect: PropTypes.func.isRequired,
  handleDateRangeSelect: PropTypes.func.isRequired,
  isActivityReportLoaded: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isAllAppointmentsInRange: PropTypes.bool.isRequired,
  isAllTasksInRange: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isPlaceUser: PropTypes.bool.isRequired,
  operatorDashboardEnabled: PropTypes.bool.isRequired,
  operatorDashboardGraphsEnabled: PropTypes.bool.isRequired,
  otherSources: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  otherStatusCounts: PropTypes.shape().isRequired,
  otherTotalLeadSourceCount: PropTypes.number.isRequired,
  powerbiEnabled: PropTypes.bool.isRequired,
  powerbiSecondAuth: PropTypes.string.isRequired,
  basePowerbiEnabled: PropTypes.bool.isRequired,
  basePowerbiSecondAuth: PropTypes.string.isRequired,
  setError: PropTypes.func.isRequired,
  setIsFetching: PropTypes.func.isRequired,
  setTasksConstraint: PropTypes.func.isRequired,
  sortBy: PropTypes.func.isRequired,
  sortDesc: PropTypes.bool.isRequired,
  sortKey: PropTypes.string.isRequired,
  textData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  userId: PropTypes.number.isRequired,
};

export default Reports;
